import { RotateCcw } from "lucide-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import { api } from "@/api/client";
import { toastApiError } from "@/api/errors";
import type { Thread } from "@/api/types";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { LoadingState } from "@/components/feedback/LoadingState";
import { ChatStream } from "@/features/threads/components/ChatStream";

interface Props {
  agentId: string;
  disabled: boolean;
}

export function SandboxTab({ agentId, disabled }: Props) {
  const { t } = useTranslation("threads");
  const { t: tc } = useTranslation("common");
  const [threadId, setThreadId] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  // Counter para forcar reload do useEffect apos reset.
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    if (disabled || !agentId) return;
    let cancelled = false;
    setLoading(true);
    setThreadId(null);
    (async () => {
      try {
        const rows = await api.GET<Thread[]>("/threads", {
          params: { agent_id: agentId, include_sandbox: true },
        });
        const existing = rows.find((t) => t.is_sandbox);
        if (cancelled) return;
        if (existing) {
          setThreadId(existing.id);
        } else {
          const created = await api.POST<Thread>("/threads", {
            type: "direct",
            agent_id: agentId,
            is_sandbox: true,
            title: "Sandbox",
          });
          if (!cancelled) setThreadId(created.id);
        }
      } catch (err) {
        toastApiError(err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [agentId, disabled, reloadKey]);

  async function handleReset() {
    if (!threadId) return;
    setResetting(true);
    try {
      await api.DELETE(`/agents/${agentId}/sandbox/threads/${threadId}`);
      toast.success(t("sandbox.resetSuccess"));
      // Triggera re-execucao do useEffect que cria nova thread.
      setReloadKey((k) => k + 1);
    } catch (err) {
      toastApiError(err);
    } finally {
      setResetting(false);
      setConfirmOpen(false);
    }
  }

  if (disabled) {
    return (
      <div className="rounded-md border border-dashed p-6 text-center text-sm text-muted-foreground">
        <p>{t("sandbox.savedFirst")}</p>
      </div>
    );
  }

  if (loading || !threadId) return <LoadingState />;

  return (
    // Altura limitada por viewport (menos o cabecalho da pagina ~240px).
    // ``min-h-0`` no scroll interno do ChatStream evita o flex
    // overflow que empurrava o composer pra fora da viewport.
    <div className="flex h-[calc(100vh-260px)] min-h-[420px] flex-col gap-3">
      <header className="flex items-center justify-between">
        <Badge variant="outline">{t("sandbox.badge")}</Badge>
        <Button
          variant="outline"
          size="sm"
          onClick={() => setConfirmOpen(true)}
          disabled={resetting}
        >
          <RotateCcw className="h-3 w-3" />
          {t("sandbox.reset")}
        </Button>
      </header>
      <div className="flex min-h-0 flex-1 overflow-hidden">
        <div className="flex min-h-0 min-w-0 flex-1 flex-col">
          <ChatStream threadId={threadId} />
        </div>
      </div>

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("sandbox.resetTitle")}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("sandbox.resetDescription")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={resetting}>
              {tc("actions.cancel")}
            </AlertDialogCancel>
            <AlertDialogAction onClick={handleReset} disabled={resetting}>
              {resetting
                ? t("sandbox.resetPending")
                : t("sandbox.resetConfirm")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
